import React, { useState } from 'react';
import { useSpring } from 'react-spring';
import styled, { useTheme } from 'styled-components';

import Button from '../Button';
import Illustration from '../Illustration';
import illustrations from '../Illustration/components';
import { Heading2, Body } from '../Typography/index';
import Blobs from './components/Blobs';
import TEST_ID from './index.testid';

export type Props = {
  dataTestId?: string;
  header: React.ReactNode;
  description?: string;
  illustration?: keyof typeof illustrations;
  buttonLabel?: React.ReactNode;
  onButtonClick?: () => void;
  blobColor?: string;
};

const EmptyStateComponent: React.FC<Props> = ({
  dataTestId,
  header,
  description,
  buttonLabel,
  illustration,
  onButtonClick,
  blobColor,
}) => {
  const theme = useTheme();
  const blobRange = [0, 1, 2, 3, 4, 5];
  const [selectedBlob, setSelectedBlob] = useState<number>(0);

  const getRandomNumberFromRange = selected => {
    const nextRandomIndex = Math.floor(Math.random() * blobRange.length);
    return nextRandomIndex === selectedBlob
      ? getRandomNumberFromRange(selected)
      : nextRandomIndex;
  };

  const [{ x }] = useSpring(
    () => ({
      from: { x: selectedBlob },
      to: async next => {
        await next({ x: selectedBlob });
      },
      config: {
        duration: 4000,
      },
      onRest: () => setSelectedBlob(getRandomNumberFromRange(selectedBlob)),
    }),
    [selectedBlob],
  );

  return (
    <Container data-testid={dataTestId}>
      <Blobs blobRange={blobRange} x={x} color={blobColor} />
      <IllustrationContainer>
        <Illustration
          name={illustration || 'box'}
          color={theme.color('text')}
          secondaryColor={theme.color('danger')}
        />
      </IllustrationContainer>
      <Heading2 margin={['l', null, null, null]}>{header}</Heading2>
      <Description margin={['xl', null]}>{description}</Description>
      <Button
        appearance="secondary"
        size="medium"
        label={buttonLabel}
        icon="plus"
        onClick={onButtonClick}
        pulse
        dataTestId={TEST_ID.ADD_ITEM_BUTTON}
      />
    </Container>
  );
};

const Container = styled.div<{}>`
  position: relative;
  width: 100%;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  margin: 100px 0;

  > :not(:first-child) {
    z-index: 100;
  }
`;

const Description = styled(Body)<{}>`
  width: 60%;
`;

const IllustrationContainer = styled.div<{}>`
  max-width: 4.5rem;
`;

export default EmptyStateComponent;
